import { useCallback, useContext } from 'react';
import { V3AppsContext } from '../../../../../../contextProviders/SplashScreenProviders/UserAndAccountProviders/V3AppsProvider';
import useGetItemNavLink from './useGetItemNavLink';
import { AppWithFieldLink } from '../types';
import appNavItemTypeCheckers from '../../../../../../types/appNavItemTypeCheckers';

export type BonusAppFieldUsage = {
  products: V3.Product[];
  apps: AppWithFieldLink[];
  workspaces: AppWithFieldLink[];
};

const useGetFieldBonusAppUsage = () => {
  const { allProductApps, allProducts, allWorkspaces } =
    useContext(V3AppsContext);
  const getItemNavLink = useGetItemNavLink();

  const getMatchingFieldLink = useCallback(
    (app: V3.App, dataset: string, field: string) => {
      const sections = app?.navigationSettings?.sections || [];
      for (const section of sections) {
        const items = section?.items || [];
        for (const item of items) {
          if (item && appNavItemTypeCheckers.isBonusHome(item)) {
            const exportSettings = item.gridExportSettings;
            if (!exportSettings) continue;
            if (exportSettings.dataset !== dataset) continue;
            if (
              Array.isArray(exportSettings.fields) &&
              exportSettings.fields.includes(field)
            ) {
              return getItemNavLink(app, item);
            }
          }
        }
      }
      return false;
    },
    [getItemNavLink],
  );

  const getFieldBonusAppUsage = useCallback(
    (dataset: string, field: string): BonusAppFieldUsage => {
      // Apps (ProductApps)
      const matchingApps: AppWithFieldLink[] = [];
      allProductApps.forEach((app) => {
        const link = getMatchingFieldLink(app, dataset, field);
        if (link) {
          matchingApps.push({ ...app, link });
        }
      });

      // Products: include if any of its appIds matches matchingApps
      const matchingProducts = allProducts.filter((p) =>
        p.appIds?.some((id) => matchingApps.some((a) => a.id === id)),
      );

      // Workspaces
      const matchingWorkspaces: AppWithFieldLink[] = [];
      allWorkspaces.forEach((w) => {
        const link = getMatchingFieldLink(w, dataset, field);
        if (link) {
          matchingWorkspaces.push({ ...w, link });
        }
      });

      return {
        products: matchingProducts,
        apps: matchingApps,
        workspaces: matchingWorkspaces,
      };
    },
    [allProductApps, allProducts, allWorkspaces, getMatchingFieldLink],
  );

  return getFieldBonusAppUsage;
};

export default useGetFieldBonusAppUsage;
